import Vue from 'vue'
import VueApollo from 'vue-apollo'
import { ApolloClient, DefaultOptions } from 'apollo-client'
import { ApolloLink } from 'apollo-link'
import {
  InMemoryCache,
  NormalizedCacheObject,
  IntrospectionFragmentMatcher,
} from 'apollo-cache-inmemory'
import { createHttpLink } from 'apollo-link-http'
import { setContext } from 'apollo-link-context'
import store from '../../store'
import { ErrorBag, GraphQLException } from '@/handler/GraphQLExceptionHandler'
import userHandler, { ErrorHandler } from '@/handler/GraphQLUserHandler'
import errorLink from './errorLink'
import { getToken } from '@/packages/cookie'
import fragmentTypes from '@/types/generated/fragmentTypes.json'

Vue.use(VueApollo)

const httpEndpoint =
  process.env.VUE_APP_GRAPHQL_HTTP || 'http://localhost:4000/graphql'
const filesRoot =
  process.env.VUE_APP_FILES_ROOT ||
  httpEndpoint.substr(0, httpEndpoint.indexOf('/graphql'))

Vue.prototype.$filesRoot = filesRoot

const defaultOptions: DefaultOptions = {
  watchQuery: {
    errorPolicy: 'all',
    fetchPolicy: 'cache-and-network',
  },
  query: {
    errorPolicy: 'all',
    fetchPolicy: 'no-cache',
  },
  mutate: {
    errorPolicy: 'all',
  },
}

const httpLink = createHttpLink({
  uri: httpEndpoint,
  credentials: 'include',
})

const authLink = setContext((_, { headers }) => {
  const token = getToken()

  return {
    headers: {
      ...headers,
      'X-XSRF-TOKEN': token || '',
      'X-Partner': store.getters['auth/partnerId'],
    },
  }
})

const fragmentMatcher = new IntrospectionFragmentMatcher({
  introspectionQueryResultData: fragmentTypes,
})

export const apolloClient = new ApolloClient({
  link: ApolloLink.from([authLink, errorLink, httpLink]),
  cache: new InMemoryCache({ fragmentMatcher }),
  defaultOptions,
})

export function createProvider() {
  const apolloProvider = new VueApollo({
    defaultClient: apolloClient,
    defaultOptions: {
      $query: {
        // fetchPolicy: 'cache-and-network',
      },
    },
    watchLoading(isLoading) {
      store.dispatch(isLoading ? 'loading/showLoading' : 'loading/hideLoading')
    },
    errorHandler(error) {
      if (process.env.NODE_ENV !== 'production') {
        console.log(
          '%cError',
          'background: red; color: white; padding: 2px 4px; border-radius: 3px; font-weight: bold;',
          error.message
        )
      }

      const errorBag = new ErrorBag()
      error.graphQLErrors.forEach((e) => {
        errorBag.add(e.extensions?.code, e.extensions?.message)
      })

      store.dispatch('errors/set', errorBag.all(), { root: true })
      store.dispatch('loading/hideLoading')
    },
  })

  return apolloProvider
}

const resetApolloClient = async (
  client: ApolloClient<NormalizedCacheObject>
) => {
  try {
    await client.clearStore()
  } catch (e) {
    console.log('%cError on cache reset', 'color: orange;', e.message)
  }
}

export async function onLogin(
  apolloClient: ApolloClient<NormalizedCacheObject>
) {
  await resetApolloClient(apolloClient)
}

export async function onLogout(
  apolloClient: ApolloClient<NormalizedCacheObject>
) {
  await resetApolloClient(apolloClient)
}

// loading wrapper
function loadable<T extends any[], U>(
  fn: (...args: T) => Promise<U>
): (...args: T) => Promise<U> {
  return async (...args: T) => {
    try {
      store.dispatch('loading/showLoading')
      return await fn(...args)
    } finally {
      store.dispatch('loading/hideLoading')
    }
  }
}

/**
 * Query
 *
 * @example
 * apolloClient.query({
 *   context: {
 *     errorHandler: (errorCode, error) {
 *       if (errorCode === 'auth.login_failed') {
 *           //handle login failed error
 *        }
 *        return error;
 *     }
 *   }
 * }
 */
const query = apolloClient.query
apolloClient.query = async (options) => {
  store.dispatch('errors/clear')
  const res = await loadable(query)(options)
  if (res.errors) {
    const handler: ErrorHandler = options.context?.errorHandler
    const unHandledErrors = userHandler(res.errors, handler)
    throw new GraphQLException(unHandledErrors)
  }
  return res
}

/**
 * Mutation
 *
 * @example
 * apolloClient.mutate({
 *   context: {
 *     errorHandler: (errorCode, error) {
 *       if (errorCode === 'auth.login_failed') {
 *           //handle login failed error
 *        }
 *        return error
 *     }
 *   }
 * }
 */
const mutate = apolloClient.mutate
apolloClient.mutate = async (options) => {
  store.dispatch('errors/clear')
  const res = await loadable(mutate)(options)
  if (res.errors) {
    const handler: ErrorHandler = options.context?.errorHandler
    const unHandledErrors = userHandler(res.errors, handler)
    throw new GraphQLException(unHandledErrors)
  }
  return res
}
